import 'cross-fetch/polyfill';
import axios from 'axios';
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  Operation,
} from '@apollo/client';
import { createLink } from 'apollo-absinthe-upload-link';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { NetworkError } from '@apollo/client/errors';
import { getStore } from 'spw-redux/store-registry';
import * as Sentry from '@sentry/react';
import { GraphQLError } from 'graphql';

const handledMessages = [
  'session_superseded',
  'not_authenticated',
  'session_expired',
];

type ApiNetworkError = NetworkError & {
  statusCode?: number;
};

class GraphqlApiError extends Error {
  constructor(networkError: ApiNetworkError, operation: Operation) {
    const message = operation?.operationName
      ? `Operation Name: ${operation.operationName}`
      : networkError?.message;

    super(message);
    this.name = networkError?.statusCode
      ? `[GraphQL API Error] Status: ${networkError.statusCode}`
      : `[GraphQL API Error] Network Error: ${networkError}`;
    this.stack = networkError?.stack;
  }
}

const errorHandler = onError(
  ({ operation, forward, networkError, graphQLErrors }) => {
    if ((networkError as ApiNetworkError)?.statusCode) {
      Sentry.captureException(
        new GraphqlApiError(networkError as ApiNetworkError, operation)
      );
    }

    return forward(operation).map((op) => {
      if (
        networkError &&
        'statusCode' in networkError &&
        networkError.statusCode === 401
      ) {
        return op;
      }
      if (graphQLErrors) {
        graphQLErrors.forEach((error: GraphQLError) => {
          const { message, locations, path } = error;

          if (
            path?.some((s) =>
              s
                .toString()
                .match(/(floorplan|floorplanUpload|getFloorplanGeojson)$/g)
            ) &&
            message === 'not_found'
          ) {
            return;
          }

          if (handledMessages.includes(message)) {
            return;
          }

          Sentry.captureException(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          );
        });
      }
      if (networkError) {
        Sentry.captureException(
          new GraphqlApiError(networkError as ApiNetworkError, operation)
        );
      }
      return op;
    });
  }
);

const authLink = setContext((_: any, ctx: any) => {
  const token = localStorage.getItem('api_token_v2')
    ? JSON.parse(localStorage.getItem('api_token_v2'))
    : null;

  return {
    headers: {
      ...ctx.headers,
      ...(token ? { authorization: `Bearer ${token.apiToken}` } : {}),
    },
  };
});

export const graphqlClient = new ApolloClient({
  link: ApolloLink.from([errorHandler, authLink, createLink({ uri: '/api' })]),
  cache: new InMemoryCache({
    addTypename: false,
  }),
  assumeImmutableResults: true,
});

export const httpClient = axios.create({
  baseURL: '/',
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
  transformRequest: (data, headers) => {
    let csfrToken;
    if (getStore()) {
      ({ csfrToken } = getStore()?.getState()?.authReducer);
    }
    // eslint-disable-next-line no-param-reassign
    headers['x-csrf-token'] = csfrToken;
    return JSON.stringify(data);
  },
});
